/* @flow */

/*
 * Random number generator with seed
 */
export class Rng {
  seed: number;

  constructor(seed: number) {
    this.seed = seed;
  }

  next(): number {
    const x = Math.sin(this.seed++) * 10000;
    return x - Math.floor(x);
  }
}

/*
 * Random value generator
 * will generate a random value of type T
 */
export class Random<T> {
  // Alway return the provided value
  static const: <R>(R) => Random<R>;

  // Will return random number between min and max
  static float: (min: number, max: number) => Random<number>;
  static int: (min: number, max: number) => Random<number>;

  // Will return random element choosed from provided values
  static of: <R>(...args: R[]) => Random<R>;

  static map2: <T1, T2, R>(r1: Random<T1>, r2: Random<T2>, f: (T1, T2) => R) => Random<R>;
  static map3: <T1, T2, T3, R>(r1: Random<T1>, r2: Random<T2>, r3: Random<T3>, f: (T1, T2, T3) => R) => Random<R>;
  static map4: <T1, T2, T3, T4, R>(
    r1: Random<T1>,
    r2: Random<T2>,
    r3: Random<T3>,
    r4: Random<T4>,
    f: (T1, T2, T3, T4) => R
  ) => Random<R>;
  static traverse: <T, R>(arr: T[], f: (T) => Random<R>) => Random<R[]>;

  /**
   * return random element from provide values, based on its probability
   * total of all probabilties must be 1
   */
  static ofProbabilities: <R>(...args: Array<[R, number]>) => Random<R>;

  // Will return a random array with specific length, using provided random generator
  static arrayOfLength: <R>(random: Random<R>, length: number) => Random<R[]>;
  // Will return a random array with random length within minLength and maxLength, using provided random generator
  static array: <R>(random: Random<R>, minLength: number, maxLength: number) => Random<R[]>;

  gen: Rng => T;

  constructor(generator: Rng => T) {
    this.gen = generator;
  }

  map<R>(f: T => R): Random<R> {
    return new Random(rng => f(this.gen(rng)));
  }

  bind<R>(f: T => Random<R>): Random<R> {
    const generator = rng => {
      const src = this.gen(rng);
      const dest = f(src);
      return dest.gen(rng);
    };

    return new Random(generator);
  }

  filter(pred: T => boolean): Random<T> {
    const generator = rng => {
      let value = this.gen(rng);
      while (!pred(value)) {
        value = this.gen(rng);
      }
      return value;
    };
    return new Random(generator);
  }
}

// Implements

Random.const = <R>(r: R): Random<R> => {
  return new Random(_ => r);
};

Random.float = (min: number, max: number): Random<number> => {
  const generator = rng => {
    const random = rng.next();
    return random * (max - min) + min;
  };

  return new Random(generator);
};

Random.int = (min: number, max: number): Random<number> => {
  const generator = rng => {
    const random = rng.next();
    return Math.floor(random * (max - min + 1)) + min;
  };

  return new Random(generator);
};

Random.of = <R>(...args: R[]): Random<R> => {
  return Random.int(0, args.length - 1).map(index => args[index]);
};

Random.ofProbabilities = <R>(...args: Array<[R, number]>): Random<R> => {
  const totalP = args.reduce((acc, [_, p]) => acc + p, 0);
  if (Math.abs(totalP - 1) > 0.000001) throw new RangeError('Total probabilities must be 1, but it is: ' + totalP);

  return Random.float(0, 1).map(random => {
    let x = random;
    for (const [r, p] of args) {
      if (x - p <= 0) return r;
      x = x - p;
    }
    throw new RangeError('Total probabilities must be 1');
  });
};

Random.arrayOfLength = <R>(random: Random<R>, length: number): Random<R[]> => {
  const generator = rng => {
    let result = [];
    for (let i = 0; i < length; i++) {
      result.push(random.gen(rng));
    }
    return result;
  };

  return new Random(generator);
};

Random.array = <R>(random: Random<R>, minLength: number, maxLength: number): Random<R[]> => {
  return Random.int(minLength, maxLength).bind(length => Random.arrayOfLength(random, length));
};

Random.map2 = <U, V, R>(r1: Random<U>, r2: Random<V>, f: (U, V) => R): Random<R> => {
  return r1.bind(v1 => r2.map(v2 => f(v1, v2)));
};

Random.map3 = <T1, T2, T3, R>(r1: Random<T1>, r2: Random<T2>, r3: Random<T3>, f: (T1, T2, T3) => R): Random<R> => {
  return Random.map2(
    r1,
    Random.map2(r2, r3, (a, b) => [a, b]),
    (x, [y, z]) => f(x, y, z)
  );
};

Random.map4 = <T1, T2, T3, T4, R>(
  r1: Random<T1>,
  r2: Random<T2>,
  r3: Random<T3>,
  r4: Random<T4>,
  f: (T1, T2, T3, T4) => R
): Random<R> => {
  return Random.map2(
    Random.map2(r1, r2, (v1, v2) => [v1, v2]),
    Random.map2(r3, r4, (v3, v4) => [v3, v4]),
    ([v1, v2], [v3, v4]) => f(v1, v2, v3, v4)
  );
};

Random.traverse = <T, R>(arr: T[], f: T => Random<R>): Random<R[]> => {
  return arr.reduce((acc, item) => Random.map2(acc, f(item), (items, item) => items.concat([item])), Random.const([]));
};
